"use client"

import getKlaviyoProfile from "@/actions/klaviyo/client/getKlaviyoProfile"
import isKlaviyoIdSubscribed from "@/actions/klaviyo/client/isKlaviyoIdSubscribed"
import addFreeShipping from "@/actions/selection/client/add-free-shipping"
import removeFreeShipping from "@/actions/selection/client/remove-free-shipping"
import { activeTextClasses } from "@/app/(site)/_components/Atoms/Inputs/input-text-classes"
import IconCheck from "@/app/(site)/_components/Atoms/Icons/IconCheck"
import IconParcel from "@/app/(site)/_components/Atoms/Icons/IconParcel"
import Input, { type InputProps } from "@/app/(site)/_components/Atoms/Inputs/Input"
import { klaviyoFormCodesMap } from "@/app/(site)/_components/Modules/Checkout/Views/Information/maps"
import useSWRBasketSessionState from "@/app/(site)/_utils/_hooks/SWR/useSWRBasketSessionState"
import { FREE_SHIPPING_VOUCHER_CODE } from "@/lib/siteconfig/constants"
import { emailPattern } from "@/lib/siteconfig/regexPatterns"
import { triggerIsSubscribiningKlaviyoEmail } from "@/lib/utils/Events/helpers"
import { t } from "@/translations"
import { clsx } from "@/src/utils/clsx"
import { useCallback, useEffect, useRef, useState } from "react"

export default function InputEmail(props: Omit<InputProps, "type">) {
    const debounceTimeoutRef = useRef<NodeJS.Timeout | null>(null)
    const [isSubscribed, setIsSubscribed] = useState<boolean>(false)

    const { mutate, selection } = useSWRBasketSessionState()

    const removeFreeShippingVoucher = useCallback(async () => {
        const removeResult = await removeFreeShipping()
        if (removeResult.success) {
            await mutate()
        }
        triggerIsSubscribiningKlaviyoEmail(false)
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    useEffect(() => {
        const hasFreeShippingVoucher =
            selection &&
            Array.isArray(selection.discounts?.vouchers) &&
            selection.discounts?.vouchers.some(
                (item: any) => item.voucher === FREE_SHIPPING_VOUCHER_CODE
            )

        const hasEmailInSelection =
            (selection?.address?.email?.length ?? 0) > 0 ||
            (selection?.shippingAddress?.email?.length ?? 0) > 0

        const email = props.value as string

        if (!(!email || !email.includes("@") || email.length <= 4)) {
            return // prevent removing the voucher
        }
        if (!hasEmailInSelection && hasFreeShippingVoucher) {
            triggerIsSubscribiningKlaviyoEmail(true)
            void removeFreeShippingVoucher()
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [JSON.stringify(selection), props.value])

    useEffect(() => {
        const hasFreeShippingVoucher =
            selection &&
            Array.isArray(selection.discounts?.vouchers) &&
            selection.discounts?.vouchers.some(
                (item: any) => item.voucher === FREE_SHIPPING_VOUCHER_CODE
            )

        const email = props.value as string

        if (debounceTimeoutRef.current) {
            clearTimeout(debounceTimeoutRef.current)
            debounceTimeoutRef.current = null
        }

        if (!email || !email.includes("@") || email.length <= 4) {
            setIsSubscribed(false)
            return
        }

        triggerIsSubscribiningKlaviyoEmail(true)
        debounceTimeoutRef.current = setTimeout(async () => {
            try {
                const profile = await getKlaviyoProfile(email)
                if (profile.data && profile.data.length > 0) {
                    const profileId = profile.data[0].id

                    const isSubscribed = await isKlaviyoIdSubscribed(profileId)

                    if (isSubscribed) {
                        setIsSubscribed(true)
                        const addResult = await addFreeShipping()
                        if (addResult.success) {
                            await mutate()
                            triggerIsSubscribiningKlaviyoEmail(false)
                            return
                        }
                        triggerIsSubscribiningKlaviyoEmail(false)
                    } else {
                        setIsSubscribed(false)
                        if (hasFreeShippingVoucher) {
                            await removeFreeShippingVoucher()
                        }
                        triggerIsSubscribiningKlaviyoEmail(false)
                        await mutate()
                        return
                    }
                } else {
                    setIsSubscribed(false)
                    if (hasFreeShippingVoucher) {
                        await removeFreeShippingVoucher()
                    }
                    triggerIsSubscribiningKlaviyoEmail(false)
                    await mutate()
                    return
                }
            } catch (error) {
                console.error("Error checking Klaviyo profile/subscription:", error)
                setIsSubscribed(false)
                if (hasFreeShippingVoucher) {
                    await removeFreeShippingVoucher()
                }
                triggerIsSubscribiningKlaviyoEmail(false)
                await mutate()
                return null
            } finally {
                triggerIsSubscribiningKlaviyoEmail(false)
            }
        }, 800)

        return () => {
            if (debounceTimeoutRef.current) {
                clearTimeout(debounceTimeoutRef.current)
                debounceTimeoutRef.current = null
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [props.value])

    const triggerKlaviyoEmailForm = useCallback(
        (e: React.MouseEvent<HTMLButtonElement>, langCode: string) => {
            e.preventDefault()

            window._klOnsite = window._klOnsite || []

            const formCode =
                klaviyoFormCodesMap.get(langCode) || klaviyoFormCodesMap.get("en")

            window._klOnsite.push(["openForm", formCode])
        },
        []
    )

    return (
        <div className="flex flex-col gap-12 md:gap-4">
            {isSubscribed ? (
                <div className="flex items-center gap-x-4 text-col-text-success Trade_Gothic_Text_14_150_0_300 md:pb-4">
                    <div className="flex aspect-square h-16 w-16 shrink-[0] items-center justify-center">
                        <IconCheck className="h-10 w-10 text-col-text-success" />
                    </div>
                    <span className="pt-3">
                        {t(props.langCode, "checkout.free_shipping_members_success")}
                    </span>
                </div>
            ) : (
                <div className="flex gap-x-4 md:items-center">
                    <IconParcel className="h-16 w-16" />
                    <div
                        className={clsx(
                            "flex flex-wrap items-start Trade_Gothic_Text_14_150_0_300 md:pt-3",
                            activeTextClasses
                        )}
                    >
                        {t(props.langCode, "checkout.free_shipping_members")}
                        <button
                            type="button"
                            className="klaviyo_form_trigger pl-4 underline Trade_Gothic_Text_Bold_14_150_0_700"
                            onClick={e => triggerKlaviyoEmailForm(e, props.langCode)}
                            onKeyDown={e => {
                                if (e.key === "Enter") {
                                    e.preventDefault()
                                    e.stopPropagation()
                                }
                            }}
                        >
                            {t(props.langCode, "footer.newsletter_sign_up")}
                        </button>
                    </div>
                </div>
            )}
            <Input
                key="shippingAddress.email"
                {...props}
                type="email"
                isEmailInput
                pattern={emailPattern}
                onBlurValidation
                validateCharacters={false}
                invalidValueCode="email_invalid"
            />
        </div>
    )
}
